import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  DatePickerComponent,
  ModalBodyComponent,
  ModalComponent,
  ModalFooterAction,
  ModalFooterComponent,
  ModalHeaderComponent,
} from '@groundworks/ngx-groundworks-ui';
import {
  ApiClient,
  CreateProjectUtilityRequest,
  ProjectUtilityInfo,
  UpdateProjectUtilityRequest,
} from '../../sdk/sdk';
import { formatUtcIsoDate } from '../../utils/iso-date';

export interface UtilityEditResult {
  action: 'updated' | 'deleted';
  id: string;
}

@Component({
  selector: 'app-utility-modal',
  imports: [
    FormsModule,
    DatePickerComponent,
    ModalComponent,
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
  ],
  templateUrl: './utility-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UtilityModalComponent {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly apiClient = inject(ApiClient);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = signal('');
  readonly isSaving = signal(false);
  readonly isDeleting = signal(false);
  readonly submitted = signal(false);

  readonly utility = signal<ProjectUtilityInfo | undefined>(undefined);
  readonly isEditMode = computed(() => !!this.utility());

  readonly description = signal('');
  readonly calledInDate = signal<string | undefined>(undefined);
  readonly digWindowStartDate = signal<string | undefined>(undefined);
  readonly digWindowEndDate = signal<string | undefined>(undefined);
  readonly confirmationNumber = signal('');

  readonly showCalledInDatePicker = signal(false);
  readonly showDigWindowStartDatePicker = signal(false);
  readonly showDigWindowEndDatePicker = signal(false);

  readonly calledInDateAsDate = computed(() => {
    const iso = this.calledInDate();
    return iso ? new Date(iso + 'T00:00:00') : undefined;
  });

  readonly digWindowStartDateAsDate = computed(() => {
    const iso = this.digWindowStartDate();
    return iso ? new Date(iso + 'T00:00:00') : undefined;
  });

  readonly digWindowEndDateAsDate = computed(() => {
    const iso = this.digWindowEndDate();
    return iso ? new Date(iso + 'T00:00:00') : undefined;
  });

  readonly hasInvalidDigWindow = computed(() => {
    const start = this.digWindowStartDate();
    const end = this.digWindowEndDate();
    return !!start && !!end && end < start;
  });

  readonly showDigWindowError = computed(() =>
    this.submitted() && this.hasInvalidDigWindow()
  );

  readonly canSave = computed(() => {
    if (this.isSaving() || this.isDeleting()) return false;
    if (this.isEditMode()) return true;
    return this.description().trim().length > 0
      && !!this.calledInDate()
      && !!this.digWindowStartDate()
      && !!this.digWindowEndDate();
  });

  readonly footerActions = computed<ModalFooterAction[]>(() => {
    const saving = this.isSaving();
    const deleting = this.isDeleting();
    const canSave = this.canSave();

    if (!this.isEditMode()) {
      return [
        {
          label: 'Save',
          type: 'primary',
          action: () => this.save(),
          loading: saving,
          disabled: !canSave,
        },
      ];
    }

    return [
      {
        label: 'Delete',
        type: 'outline-danger',
        action: () => this.delete(),
        loading: deleting,
        disabled: saving || deleting,
      },
      {
        label: 'Update',
        type: 'primary',
        action: () => this.update(),
        loading: saving,
        disabled: !canSave,
      },
    ];
  });

  loadUtility(): void {
    const util = this.utility();
    if (!util) return;
    this.description.set(util.description ?? '');
    this.calledInDate.set(util.calledInDate ? formatUtcIsoDate(util.calledInDate) : undefined);
    this.digWindowStartDate.set(util.digWindowStartDate ? formatUtcIsoDate(util.digWindowStartDate) : undefined);
    this.digWindowEndDate.set(util.digWindowEndDate ? formatUtcIsoDate(util.digWindowEndDate) : undefined);
    this.confirmationNumber.set(util.confirmationNumber ?? '');
  }

  toggleDatePicker(field: 'calledIn' | 'digStart' | 'digEnd'): void {
    if (field === 'calledIn') {
      this.showCalledInDatePicker.update(v => !v);
      this.showDigWindowStartDatePicker.set(false);
      this.showDigWindowEndDatePicker.set(false);
    } else if (field === 'digStart') {
      this.showDigWindowStartDatePicker.update(v => !v);
      this.showCalledInDatePicker.set(false);
      this.showDigWindowEndDatePicker.set(false);
    } else {
      this.showDigWindowEndDatePicker.update(v => !v);
      this.showCalledInDatePicker.set(false);
      this.showDigWindowStartDatePicker.set(false);
    }
  }

  handleDateSelected(field: 'calledIn' | 'digStart' | 'digEnd', date: Date | null): void {
    const iso = date ? this.toIsoString(date) : undefined;

    if (field === 'calledIn') {
      this.calledInDate.set(iso);
      this.showCalledInDatePicker.set(false);
    } else if (field === 'digStart') {
      this.digWindowStartDate.set(iso);
      this.showDigWindowStartDatePicker.set(false);
    } else {
      this.digWindowEndDate.set(iso);
      this.showDigWindowEndDatePicker.set(false);
    }
  }

  formatDateForDisplay(iso: string | undefined): string {
    if (!iso) return '';
    const [year, month, day] = iso.split('-');
    if (!year || !month || !day) return iso;
    return `${month}/${day}/${year}`;
  }

  save(): void {
    this.submitted.set(true);
    if (!this.canSave() || this.hasInvalidDigWindow()) return;
    this.isSaving.set(true);

    const request = new CreateProjectUtilityRequest({
      description: this.description() || undefined,
      calledInDate: this.toDateOrUndefined(this.calledInDate()),
      digWindowStartDate: this.toDateOrUndefined(this.digWindowStartDate()),
      digWindowEndDate: this.toDateOrUndefined(this.digWindowEndDate()),
      confirmationNumber: this.confirmationNumber() || undefined,
    });

    this.apiClient.createProjectUtility(this.projectId(), request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.isSaving.set(false);
          this.activeModal.close(result);
        },
        error: () => this.isSaving.set(false),
      });
  }

  update(): void {
    this.submitted.set(true);
    if (!this.canSave() || this.hasInvalidDigWindow()) return;
    const util = this.utility();
    if (!util) return;

    this.isSaving.set(true);

    const request = new UpdateProjectUtilityRequest({
      description: this.description() || undefined,
      calledInDate: this.toDateOrUndefined(this.calledInDate()),
      clearCalledInDate: !this.calledInDate() && !!util.calledInDate,
      digWindowStartDate: this.toDateOrUndefined(this.digWindowStartDate()),
      clearDigWindowStartDate: !this.digWindowStartDate() && !!util.digWindowStartDate,
      digWindowEndDate: this.toDateOrUndefined(this.digWindowEndDate()),
      clearDigWindowEndDate: !this.digWindowEndDate() && !!util.digWindowEndDate,
      confirmationNumber: this.confirmationNumber() || undefined,
      clearConfirmationNumber: !this.confirmationNumber() && !!util.confirmationNumber,
      isCompleted: undefined,
    });

    this.apiClient.updateProjectUtility(this.projectId(), util.id, request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.activeModal.close({ action: 'updated', id: util.id } as UtilityEditResult);
        },
        error: () => this.isSaving.set(false),
      });
  }

  delete(): void {
    const util = this.utility();
    if (!util) return;

    this.isDeleting.set(true);

    this.apiClient.deleteProjectUtility(this.projectId(), util.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.activeModal.close({ action: 'deleted', id: util.id } as UtilityEditResult);
        },
        error: () => this.isDeleting.set(false),
      });
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  private toIsoString(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private toDateOrUndefined(iso: string | undefined): Date | undefined {
    return iso ? new Date(iso + 'T00:00:00') : undefined;
  }
}
